import React, { FC, useEffect, useMemo, useState } from 'react';
import { Tab, TabProps } from 'semantic-ui-react';
import { useCentralCalls } from '@src/rest/CentralCalls';
import PracticeTab from './PracticeTab';
import type { OfferingOutline } from './ClientOutlineTypes';

const UNASSIGNED_PRACTICE_ID = '__unassigned';

interface PracticeTabMenuProps {
    clientId: string;
    sowId: string;
    offerings: OfferingOutline[];
    onOpenAddOfferingDialog?: () => void;
    disableAddOffering?: boolean;
}

interface PracticeItem {
    id: string;
    name: string;
}

type ApiRow = Record<string, unknown>;

const getCaseInsensitiveValue = ( row: ApiRow, key: string ): unknown => {
    const actualKey = Object.keys( row ?? {} ).find( k => k.toLowerCase() === key.toLowerCase() );
    return actualKey ? row[ actualKey ] : undefined;
};

const asId = ( value: unknown ): string => String( value ?? '' ).trim().toLowerCase();

const normalizePractices = ( rows: ApiRow[] ): PracticeItem[] =>
    ( Array.isArray( rows ) ? rows : [] )
        .map( row => ( {
            id: asId( getCaseInsensitiveValue( row, 'id' ) ),
            name: String( getCaseInsensitiveValue( row, 'name' ) ?? '' ).trim(),
        } ) )
        .filter( practice => !!practice.id && !!practice.name )
        .sort( ( a, b ) => a.name.localeCompare( b.name ) );

// OfferingId -> PracticeId, used when the outline payload doesn't carry PracticeId.
const buildOfferingPracticeMap = ( rows: ApiRow[] ): Record<string, string> => {
    const map: Record<string, string> = {};
    ( Array.isArray( rows ) ? rows : [] ).forEach( row => {
        const offeringId = asId( getCaseInsensitiveValue( row, 'id' ) );
        const practiceId = asId( getCaseInsensitiveValue( row, 'practiceId' ) );
        if( offeringId && practiceId ) map[ offeringId ] = practiceId;
    } );
    return map;
};

const PracticeTabMenu: FC<PracticeTabMenuProps> = ( {
    clientId,
    sowId,
    offerings,
    onOpenAddOfferingDialog,
    disableAddOffering,
} ) => {
    const { TableSelect } = useCentralCalls();

    const [ activeIndex, setActiveIndex ]                   = useState( 0 ),
          [ practices, setPractices ]                       = useState<PracticeItem[]>( [] ),
          [ offeringPracticeMap, setOfferingPracticeMap ]   = useState<Record<string, string>>( {} ),
          [ isLoading, setIsLoading ]                       = useState( true ),
          [ loadError, setLoadError ]                       = useState( false );

    useEffect( () => {
        let cancelled = false;

        const load = async () => {
            try {
                setIsLoading( true );
                const [ practiceRows, offeringRows ] = await Promise.all( [
                    TableSelect( 'Practices', {} ) as Promise<ApiRow[]>,
                    TableSelect( 'Offerings', {} ) as Promise<ApiRow[]>,
                ] );
                if( cancelled ) return;

                setPractices( normalizePractices( practiceRows ) );
                setOfferingPracticeMap( buildOfferingPracticeMap( offeringRows ) );
                setLoadError( false );
            } catch( err ){
                if( cancelled ) return;
                console.error( '[PracticeTabMenu] failed to load Practices/Offerings:', err );
                setLoadError( true );
            } finally {
                if( !cancelled ) setIsLoading( false );
            }
        };

        load();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [] );

    const offeringsByPractice = useMemo( () => {
        const grouped: Record<string, OfferingOutline[]> = {};
        offerings.forEach( offering => {
            const practiceId = asId( offering.PracticeId )
                || offeringPracticeMap[ asId( offering.OfferingId ) ]
                || UNASSIGNED_PRACTICE_ID;
            if( !grouped[ practiceId ] ) grouped[ practiceId ] = [];
            grouped[ practiceId ].push( offering );
        } );
        return grouped;
    }, [ offerings, offeringPracticeMap ] );

    // Offerings whose practice isn't in the Practices table get their own tab so
    // they never silently disappear.
    const practiceTabs = useMemo( () => {
        const known = new Set( practices.map( practice => practice.id ) );
        const hasOrphans = Object.keys( offeringsByPractice ).some( id => !known.has( id ) );
        return hasOrphans
            ? [ ...practices, { id: UNASSIGNED_PRACTICE_ID, name: 'Unassigned' } ]
            : practices;
    }, [ practices, offeringsByPractice ] );

    if( isLoading ){
        return <div className={ 'document-version-empty' }>Loading practices...</div>;
    }

    if( loadError || practiceTabs.length === 0 ){
        return (
            <div className={ 'document-version-empty' }>
                { loadError ? 'Could not load practices.' : 'No practices set up yet.' }
            </div>
        );
    }

    const knownPracticeIds = new Set( practices.map( practice => practice.id ) );
    const offeringsForTab = ( practiceId: string ): OfferingOutline[] =>
        practiceId === UNASSIGNED_PRACTICE_ID
            ? Object.entries( offeringsByPractice )
                .filter( ( [ id ] ) => !knownPracticeIds.has( id ) )
                .reduce<OfferingOutline[]>( ( all, [ , list ] ) => all.concat( list ), [] )
            : offeringsByPractice[ practiceId ] ?? [];

    const panes: TabProps[ 'panes' ] = practiceTabs.map( practice => {
        const practiceOfferings = offeringsForTab( practice.id );

        return {
            menuItem: {
                key: practice.id,
                content: (
                    <span className={ 'practice-tab-label' }>
                        { practice.name }
                        <span className={ `practice-tab-count${ practiceOfferings.length === 0 ? ' is-empty' : '' }` }>
                            { practiceOfferings.length }
                        </span>
                    </span>
                ),
            },
            render: () => (
                <Tab.Pane attached={ false }>
                    <PracticeTab
                        key={ practice.id }
                        clientId={ clientId }
                        sowId={ sowId }
                        practiceName={ practice.name }
                        offerings={ practiceOfferings }
                        onOpenAddOfferingDialog={ onOpenAddOfferingDialog }
                        disableAddOffering={ disableAddOffering }
                    />
                </Tab.Pane>
            ),
        };
    } );

    // Clamp in case the practice list shrank while a later tab was selected.
    const safeActiveIndex = Math.min( activeIndex, practiceTabs.length - 1 );

    const handleTabChange = ( _event: React.MouseEvent<HTMLDivElement>, data: TabProps ) => {
        const newIndex = typeof data.activeIndex === 'number' ? data.activeIndex : Number( data.activeIndex );
        setActiveIndex( newIndex );
    };

    return (
        <div className={ 'consultant-tab-menu-with-action consultant-tab-menu-with-action-practice' }>
            <Tab
                menu={ { secondary: true, pointing: true } }
                panes={ panes }
                renderActiveOnly
                activeIndex={ safeActiveIndex }
                onTabChange={ handleTabChange }
            />
        </div>
    );
};

export default PracticeTabMenu;
